import { useQuery } from 'react-query';
import useAuth from '../use-auth';
import {
  getAssessmentTest,
  getTeamsProject,
  getTeamsProjectsDashboard,
  getTeamsDashboard,
  getTeamsActivity,
} from '../../api/teams';

export const queryOptions = {
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export function useAssessmentTest(companySlug: string, testSlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-assessment-test', isAuthenticated, companySlug, testSlug],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const test = await getAssessmentTest(token, {
        companySlug,
        testSlug,
      });
      return test;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useTeamsProject(projectSlug: string, companySlug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const state = useQuery(
    ['get-teams-project', isAuthenticated, projectSlug, companySlug],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      if (!companySlug) {
        return null;
      }
      const token = await getTokenSilently();
      const project = await getTeamsProject(token, {
        projectSlug,
        companySlug,
      });

      return project;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useTeamsProjectsDashboard() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const state = useQuery(
    ['get-teams-projects-dashboard', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const projects = await getTeamsProjectsDashboard(token);
      return projects;
    },
    queryOptions
  );
  return {
    value: state.data,
    retry: state.refetch,
    loading: state.isLoading,
    error: state.error,
  };
}

export function useTeamsActivity({
  teamSlug,
  trackSlug,
  missionSlug,
  activitySlug,
}: {
  teamSlug?: string;
  trackSlug: string;
  missionSlug: string;
  activitySlug: string;
}) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    [
      'get-teams-activity',
      isAuthenticated,
      teamSlug,
      trackSlug,
      missionSlug,
      activitySlug,
    ],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const activity = await getTeamsActivity(token, {
        trackSlug,
        missionSlug,
        activitySlug,
      });
      return activity;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}

export function useTeamsDashboard({
  trackSlug,
  teamSlug,
}: {
  trackSlug: string;
  teamSlug?: string;
  moduleSlug?: string;
}) {
  const { getTokenSilently, isAuthenticated } = useAuth();
  const state = useQuery(
    ['get-track-stats', isAuthenticated, trackSlug, teamSlug],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      const stats = await getTeamsDashboard(token, { trackSlug });
      return stats;
    },
    queryOptions
  );
  return {
    value: state.data,
    loading: state.isLoading,
    error: state.error,
    retry: state.refetch,
  };
}
